import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Tag,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  History,
  XCircle,
  Link2,
  Clock,
  Unlink,
  Layers,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Hash,
  Archive,
} from "lucide-react";
import { format } from "date-fns";
import Loader from "../../loader/Loader";
import AddProductionTagging from "./AddProductionTagging";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";

// ─── Color Map & Helpers ──────────────────────────────────────────────────────
const COLOR_MAP = {
  blue: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200" },
  amber: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  slate: {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  violet: {
    bg: "bg-violet-100",
    text: "text-violet-700",
    border: "border-violet-200",
  },
  emerald: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  rose: { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-200" },
  cyan: { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-200" },
  orange: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    border: "border-orange-200",
  },
  yellow: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    border: "border-yellow-200",
  },
};

const getColor = (color) => COLOR_MAP[color] ?? COLOR_MAP.slate;

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return format(new Date(dateString), "MMM dd, yyyy");
};

const TagPill = ({ tagName, color }) => {
  const c = getColor(color);
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded border ${c.bg} ${c.text} ${c.border} uppercase tracking-wide`}
    >
      <Tag size={10} />
      {tagName}
    </span>
  );
};

// ─── History Modal ───────────────────────────────────────────────────────────
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [auditItem, setAuditItem] = useState(null);
  const itemsPerPage = 5;

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${domain}/api/ProductionTaggings/history`);
      setHistoryData(res.data || []);
    } catch {
      toast.error("Failed to load tagging history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const filtered = historyData.filter((item) => {
    const q = search.toLowerCase();
    return (
      (item.tagName || "").toLowerCase().includes(q) ||
      (item.groupName || "").toLowerCase().includes(q) ||
      (item.linkedProducts || []).some((lp) =>
        lp.productName.toLowerCase().includes(q),
      )
    );
  });

  const currentItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-xl">
              <History size={18} className="text-gray-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">
                Archived Tags
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Restore soft-deleted production tags
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search deleted tags, groups, or linked products..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Archive size={22} className="text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">
                No archived records found.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-bold text-gray-800 text-sm flex items-center gap-1">
                          <Layers size={14} className="text-gray-400" />{" "}
                          {item.groupName}
                        </span>
                        <ChevronRight size={12} className="text-gray-300" />
                        <TagPill tagName={item.tagName} color={item.color} />
                        <button
                          onClick={() => setAuditItem(item)}
                          className="text-amber-500 hover:text-amber-700 ml-1"
                          title="View Audit Info"
                        >
                          <Clock size={13} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {item.linkedProducts?.length > 0 ? (
                          item.linkedProducts.map((lp) => (
                            <span
                              key={lp.productId}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded"
                            >
                              <Link2 size={10} /> {lp.productName} ({lp.uomName}
                              )
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Unlink size={11} /> No linked products
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span>
                          Deleted by:{" "}
                          <strong className="text-red-500">
                            {item.deletedByFullName || "Unknown"}
                          </strong>
                        </span>
                        <span>Date: {formatDate(item.deletedDate)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <button
                        onClick={async () => {
                          await onRestore(item.id);
                          fetchHistory();
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition"
                      >
                        <RotateCcw size={12} /> Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="pt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.tagName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ─── Main View Component ──────────────────────────────────────────────────────
const AllProductionTagging = () => {
  const currentUserID = useSelector(selectUserID);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Adjust based on group count preference

  // Modals
  const [taggingToEdit, setTaggingToEdit] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetching Grouped Data natively from the endpoint you just created
      const res = await axios.get(`${domain}/api/ProductionTaggings/grouped`);
      setGroupedData(res.data || []);
    } catch {
      toast.error("Failed to fetch production tags.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtering Logic (Matches either Group Name, Tag Name, or Linked Product)
  const filteredGroups = groupedData
    .map((group) => {
      const q = searchTerm.toLowerCase();
      const matchesGroup = group.groupName.toLowerCase().includes(q);

      const matchingTags = group.tags.filter(
        (t) =>
          t.tagName.toLowerCase().includes(q) ||
          (t.linkedProducts || []).some(
            (lp) =>
              lp.productName.toLowerCase().includes(q) ||
              (lp.itemCode || "").toLowerCase().includes(q),
          ),
      );

      // If search matches the group header, return it with all its tags
      if (matchesGroup) return group;

      // If it only matches specific tags, return the group containing only those tags
      if (matchingTags.length > 0) return { ...group, tags: matchingTags };

      return null;
    })
    .filter(Boolean);

  const currentGroups = filteredGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const toggleExpandGroup = (groupName) => {
    setExpandedGroup(expandedGroup === groupName ? null : groupName);
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Archive this Tag? It can be restored later.")) return;
    try {
      await axios.put(`${domain}/api/ProductionTaggings/soft-delete/${id}`, {
        userID: currentUserID,
      });
      toast.success("Tag archived.");
      fetchData();
    } catch {
      toast.error("Failed to delete tag.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/ProductionTaggings/restore/${id}`, {
        userID: currentUserID,
      });
      toast.success("Tag restored successfully.");
      fetchData();
    } catch {
      toast.error("Failed to restore tag.");
    }
  };

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Page Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-100 rounded-2xl shadow-sm">
                <Tag size={22} className="text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 leading-tight">
                  Production Tagging
                </h1>
                <p className="text-gray-500 text-sm">
                  Organize material tags by category and link inventory items.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Archived</span>
              </button>

              <button
                onClick={() => {
                  setTaggingToEdit(null);
                  setIsAddModalVisible(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition shadow-md hover:shadow-lg"
              >
                <Plus size={16} />
                Add Tag Group
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* ── Search Bar ── */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Section Header, Tag Name, or Linked Product..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all text-sm outline-none"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
                setExpandedGroup(null); // Close accordion when searching
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            {/* ── Desktop & Mobile Group Accordion Table ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider hidden md:table-header-group">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">
                      Section Header (Group)
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Tags Count
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentGroups.map((group) => {
                    const isExpanded = expandedGroup === group.groupName;

                    return (
                      <React.Fragment key={group.groupName}>
                        {/* Parent Group Row */}
                        <tr
                          onClick={() => toggleExpandGroup(group.groupName)}
                          className={`hover:bg-indigo-50/30 transition cursor-pointer ${isExpanded ? "bg-indigo-50/20" : ""}`}
                        >
                          <td className="px-4 md:px-5 py-4 text-center w-10">
                            <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                              {isExpanded ? (
                                <ChevronDown size={18} />
                              ) : (
                                <ChevronRight size={18} />
                              )}
                            </button>
                          </td>
                          <td className="px-2 md:px-5 py-4">
                            <div className="flex items-center gap-2">
                              <Layers
                                size={16}
                                className="text-indigo-400 hidden md:block"
                              />
                              <span className="font-bold text-gray-900 md:text-sm text-base">
                                {group.groupName}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-center hidden md:table-cell">
                            <span className="inline-flex items-center justify-center bg-gray-100 text-gray-600 font-bold px-2.5 py-1 rounded-full text-xs">
                              {group.tags.length} Tags
                            </span>
                          </td>
                        </tr>

                        {/* Expanded Tags Sub-Table */}
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan="3"
                              className="bg-slate-50/70 px-4 py-4 md:px-10 md:py-6 border-b border-gray-200"
                            >
                              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                <table className="w-full text-xs text-left text-gray-600">
                                  <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100 hidden sm:table-header-group">
                                    <tr>
                                      <th className="px-4 py-3">Tag Display</th>
                                      <th className="px-4 py-3">
                                        Linked Products
                                      </th>
                                      <th className="px-4 py-3 text-center">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {group.tags.map((tag) => (
                                      <tr
                                        key={tag.id}
                                        className="hover:bg-gray-50 transition block sm:table-row"
                                      >
                                        {/* Mobile Stacking Display */}
                                        <td className="px-4 py-3 block sm:table-cell border-b sm:border-0 border-gray-50">
                                          <div className="flex flex-col gap-1 items-start">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase sm:hidden block mb-1">
                                              Tag Display
                                            </span>
                                            <TagPill
                                              tagName={tag.tagName}
                                              color={tag.color}
                                            />
                                            <span className="text-[10px] font-mono text-gray-400 mt-0.5">
                                              Unit:{" "}
                                              <strong className="text-gray-600 uppercase">
                                                {tag.unit}
                                              </strong>
                                            </span>
                                          </div>
                                        </td>

                                        <td className="px-4 py-3 block sm:table-cell border-b sm:border-0 border-gray-50">
                                          <span className="text-[10px] text-gray-400 font-bold uppercase sm:hidden block mb-1.5">
                                            Linked Products
                                          </span>
                                          {tag.linkedProducts?.length > 0 ? (
                                            <div className="flex flex-wrap gap-1.5">
                                              {tag.linkedProducts.map((lp) => (
                                                <span
                                                  key={lp.productId}
                                                  className="inline-flex items-center gap-1 font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded text-[11px]"
                                                >
                                                  <Link2 size={10} />{" "}
                                                  {lp.productName}{" "}
                                                  <span className="opacity-60 font-mono">
                                                    ({lp.uomName})
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="inline-flex items-center gap-1.5 text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded text-[11px]">
                                              <Unlink size={10} /> Unlinked
                                            </span>
                                          )}
                                        </td>

                                        <td className="px-4 py-3 block sm:table-cell">
                                          <div className="flex items-center sm:justify-center justify-end gap-1.5 mt-2 sm:mt-0">
                                            <button
                                              onClick={() => setAuditItem(tag)}
                                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                                              title="Audit Info"
                                            >
                                              <Clock size={14} />
                                            </button>
                                            <button
                                              onClick={() => {
                                                setTaggingToEdit(tag);
                                                setIsAddModalVisible(true);
                                              }}
                                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                              title="Edit"
                                            >
                                              <Edit size={14} />
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleSoftDelete(tag.id)
                                              }
                                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                              title="Archive"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {currentGroups.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No groups found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Try adjusting your search criteria.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredGroups.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      {isAddModalVisible && (
        <AddProductionTagging
          onClose={() => {
            setIsAddModalVisible(false);
            fetchData();
          }}
          refreshData={fetchData}
          taggingToEdit={taggingToEdit}
        />
      )}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.tagName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllProductionTagging;
